import { Message } from "../types/Types";

export type OutboundMessageStatus = "sent" | "delivered" | "seen";

const STATUS_RANK: Record<OutboundMessageStatus, number> = {
  sent: 0,
  delivered: 1,
  seen: 2,
};

const STORAGE_PREFIX = "smbf.outboundMessageStatus.";
const FUZZY_MATCH_MS = 2000;

export function parseMessageDateMs(dateSent: string): number {
  const ms = new Date(dateSent || "").getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** Stable key across client ISO strings and API DateTime serialization. */
export function outboundStatusKey(
  dateSent: string,
  peerUserId: number
): string {
  const ms = parseMessageDateMs(dateSent);
  if (ms > 0) return `${peerUserId}|${ms}`;
  return `${peerUserId}|raw:${dateSent}`;
}

export function mergeOutboundStatus(
  a: OutboundMessageStatus,
  b: OutboundMessageStatus
): OutboundMessageStatus {
  return STATUS_RANK[a] >= STATUS_RANK[b] ? a : b;
}

export function mergeOutboundStatusMaps(
  ...maps: Array<Record<string, OutboundMessageStatus>>
): Record<string, OutboundMessageStatus> {
  const result: Record<string, OutboundMessageStatus> = {};
  for (const map of maps) {
    for (const [key, status] of Object.entries(map)) {
      result[key] = result[key]
        ? mergeOutboundStatus(result[key], status)
        : status;
    }
  }
  return result;
}

export function loadPersistedOutboundStatus(
  userObjectId: string
): Record<string, OutboundMessageStatus> {
  if (!userObjectId) return {};
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${userObjectId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: Record<string, OutboundMessageStatus> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (value === "sent" || value === "delivered" || value === "seen") {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function savePersistedOutboundStatus(
  userObjectId: string,
  status: Record<string, OutboundMessageStatus>
): void {
  if (!userObjectId) return;
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${userObjectId}`,
      JSON.stringify(status)
    );
  } catch {
    /* quota / private mode */
  }
}

export function lookupOutboundStatus(
  dateSent: string,
  peerUserId: number,
  liveStatus: Record<string, OutboundMessageStatus>
): OutboundMessageStatus | undefined {
  const exactKey = outboundStatusKey(dateSent, peerUserId);
  const exact = liveStatus[exactKey];
  if (exact) return exact;

  const targetMs = parseMessageDateMs(dateSent);
  if (!targetMs) return undefined;

  const prefix = `${peerUserId}|`;
  let best: OutboundMessageStatus | undefined;
  for (const [key, status] of Object.entries(liveStatus)) {
    if (!key.startsWith(prefix)) continue;
    const keyMs = Number(key.slice(prefix.length));
    if (Number.isNaN(keyMs)) continue;
    if (Math.abs(keyMs - targetMs) <= FUZZY_MATCH_MS) {
      best = best ? mergeOutboundStatus(best, status) : status;
    }
  }
  return best;
}

/** Peer replied after this message — infer “seen” when no stored receipt exists. */
export function appearsSeenByReply(
  sentMsg: Message,
  sortedMessages: Message[],
  userId: number | undefined,
  guid: string | null
): boolean {
  const sentAt = parseMessageDateMs(sentMsg.dateSent);
  if (!sentAt) return false;

  return sortedMessages.some((m) => {
    const fromSelf =
      m.userIdMessageFrom?.toString() === userId?.toString() ||
      m.userIdMessageFrom?.toString() === guid;
    if (fromSelf) return false;
    return parseMessageDateMs(m.dateSent) > sentAt;
  });
}

export function inferOutboundStatusFromMessages(
  messages: Message[],
  userId: number | undefined,
  guid: string | null
): Record<string, OutboundMessageStatus> {
  if (!messages.length || userId == null) return {};

  const sorted = [...messages].sort(
    (a, b) => parseMessageDateMs(a.dateSent) - parseMessageDateMs(b.dateSent)
  );
  const inferred: Record<string, OutboundMessageStatus> = {};

  for (const msg of sorted) {
    const fromSelf =
      msg.userIdMessageFrom?.toString() === userId?.toString() ||
      msg.userIdMessageFrom?.toString() === guid;
    if (!fromSelf) continue;

    const peerId = Number(msg.userIdMessageTo);
    if (appearsSeenByReply(msg, sorted, userId, guid)) {
      const key = outboundStatusKey(msg.dateSent, peerId);
      inferred[key] = "seen";
    }
  }

  return inferred;
}

export function resolveOutboundMessageStatus(
  sentMsg: Message,
  peerUserId: number,
  sortedMessages: Message[],
  userId: number | undefined,
  guid: string | null,
  liveStatus: Record<string, OutboundMessageStatus>
): OutboundMessageStatus {
  const tracked = lookupOutboundStatus(
    sentMsg.dateSent,
    peerUserId,
    liveStatus
  );

  if (tracked === "seen" || appearsSeenByReply(sentMsg, sortedMessages, userId, guid)) {
    return "seen";
  }
  if (tracked === "delivered") return "delivered";
  return tracked || "sent";
}

export function markMessagesSeenUpTo(
  prev: Record<string, OutboundMessageStatus>,
  peerUserId: number,
  messages: Message[],
  userId: number | undefined,
  guid: string | null,
  lastSeenDateSent: string
): Record<string, OutboundMessageStatus> {
  const seenAt = parseMessageDateMs(lastSeenDateSent);
  if (!seenAt) return prev;

  const next = { ...prev };
  for (const msg of messages) {
    const fromSelf =
      msg.userIdMessageFrom?.toString() === userId?.toString() ||
      msg.userIdMessageFrom?.toString() === guid;
    if (!fromSelf) continue;
    if (Number(msg.userIdMessageTo) !== Number(peerUserId)) continue;

    const msgAt = parseMessageDateMs(msg.dateSent);
    if (msgAt > 0 && msgAt <= seenAt) {
      const key = outboundStatusKey(msg.dateSent, peerUserId);
      next[key] = "seen";
    }
  }
  return next;
}

export function applyOutboundStatusPatch(
  prev: Record<string, OutboundMessageStatus>,
  patch: Record<string, OutboundMessageStatus>
): Record<string, OutboundMessageStatus> {
  const next = { ...prev };
  for (const [key, status] of Object.entries(patch)) {
    next[key] = next[key] ? mergeOutboundStatus(next[key], status) : status;
  }
  return next;
}
